import { useEffect, useState, type CSSProperties } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { CtaBanner } from '~/components/sections';
import { Icon } from '~/components/ui';
import { learnCategories, learnArticleHref } from '~/content/learn';
import { cn } from '~/lib/cn';
import { getArticles } from '~/lib/payload';
import { pageHeadMeta } from '~/lib/seo';
import helpStylesHref from '~/styles/help.css?url';

const categoryFilters = learnCategories.map((category) => category.label);

const SEARCH_PLACEHOLDER = 'Search articles';
/** Placeholder char cascade starts as the pill opens mid-way. */
const PLACEHOLDER_CHAR_BASE_MS = 300;
/** Delay between consecutive placeholder characters. */
const PLACEHOLDER_CHAR_STEP_MS = 28;
/** Matches `.learn-char-in` animation duration in global.css. */
const PLACEHOLDER_CHAR_ANIM_MS = 400;

export const Route = createFileRoute('/help')({
  loader: () => getArticles(),
  head: () =>
    pageHeadMeta({
      title: 'Learn with nsave – Step by Step Guides / Articles',
      description:
        'Browse our library of step by step guides on how to use your nsave app. Learn how to open your account, receive transfers, send money and invest easily with nsave.',
      path: '/help',
      stylesheets: [helpStylesHref],
    }),
  component: HelpPage,
});

function HelpPage() {
  const articles = Route.useLoaderData();
  const [selected, setSelected] = useState<string>(categoryFilters[0]);
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;

  // While searching we match across ALL articles (title, excerpt and category
  // name); otherwise we fall back to the selected category's articles.
  // Category compare is trimmed so CMS values with stray whitespace still match
  // the sidebar labels from `learnCategories`.
  const visibleArticles = isSearching
    ? (() => {
        const needle = trimmedQuery.toLowerCase();
        return articles.filter((article) =>
          [article.title, article.excerpt, article.category].some((field) =>
            field.toLowerCase().includes(needle),
          ),
        );
      })()
    : articles.filter((article) => article.category.trim() === selected);

  // Heading above the list: the typed term while searching, else the category.
  const listHeading = isSearching ? trimmedQuery : selected;

  /** Pick a sidebar category and exit search mode so the list actually filters. */
  const selectCategory = (label: string) => {
    setSelected(label);
    setQuery('');
  };

  return (
    <PageShell>
      {/* Hero: centred headline with a persistent search bar beneath it that
          live-filters the resources list below. */}
      <Section className="pt-16 !pb-0 md:pt-24">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-title-m">
            Hi, how can we help?
          </h1>
        </div>
        <HeroSearch query={query} onQueryChange={setQuery} />
      </Section>

      {/* Resources index: categories sidebar (left), article list (centre) and
          an empty reserved column (right) on the 9-col grid. Below lg the
          categories collapse into a wrapping row above the full-width list. */}
      <Section className="!pt-[96px] !pb-[160px] md:!pt-[144px]">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-9 lg:gap-5">
          {/* Left: categories filter (cols 1–2 on lg). Sticky on desktop. On
              mobile the buttons sit in a single horizontally-scrollable row;
              md and up keep the existing wrapping row / desktop sidebar. */}
          <div className="mb-6 md:mb-0 lg:col-span-2 lg:sticky lg:top-24 lg:self-start">
            <h2 className="text-[15px] font-medium tracking-[0.27px] text-grey-500 uppercase">
              Categories
            </h2>
            <div className="group/col mt-8 flex flex-row flex-nowrap gap-x-6 gap-y-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:overflow-visible lg:mt-10 lg:flex-col lg:gap-1">
              {categoryFilters.map((label) => {
                const category = learnCategories.find((item) => item.label === label);
                const isSelected = label === selected;
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => selectCategory(label)}
                    className={cn(
                      // Idle: active = grey-1000, inactive = grey-700. On column
                      // hover: hovered item = grey-1000, all others = grey-500.
                      // Icon uses `tint` so it inherits (and transitions with) the
                      // text color. Scoped to this sidebar only.
                      'flex shrink-0 cursor-pointer items-center gap-3 py-2 text-body transition-colors ease-nav duration-150 group-has-[button:hover]/col:text-grey-500 hover:!text-grey-1000 lg:w-full',
                      isSelected ? 'font-medium text-grey-1000' : 'text-grey-700',
                    )}>
                    {category ? (
                      <Icon name={category.icon} tint className="size-5 shrink-0" />
                    ) : (
                      <span aria-hidden="true" className="size-5 shrink-0" />
                    )}
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Centre: article list (cols 3–7 on lg). Heading shows the typed
              search term while searching, otherwise the selected category. */}
          <div className="lg:col-span-5 lg:col-start-3">
            <h2 className="text-[15px] font-medium tracking-[0.27px] text-grey-500 uppercase">
              {listHeading}
            </h2>
            {visibleArticles.length > 0 ? (
              <ul key={listHeading} className="group/list mt-4 flex flex-col">
                {visibleArticles.map((article, i) => (
                  <li
                    key={article.fullPath}
                    style={{ '--stagger': `${i * 30}ms` } as CSSProperties}
                    className="learn-list-item-in border-b border-black-6 first:border-t">
                    <Link
                      to={learnArticleHref(article)}
                      className="group flex flex-col gap-4 py-8 transition-opacity ease-nav duration-150 group-has-[a:hover]/list:opacity-40 hover:!opacity-100">
                      <h3 className="text-h5 font-semibold tracking-[-0.02em] text-ink">
                        <span className="link-underline-lr bg-origin-content">{article.title}</span>
                      </h3>
                      <p className="text-body text-grey-900">{article.excerpt}</p>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-body-s font-medium text-grey-500">
                          {article.date}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-8 text-body text-grey-700">
                {isSearching
                  ? `No results for “${trimmedQuery}”`
                  : 'No articles in this category yet.'}
              </p>
            )}
          </div>

          {/* Right: reserved column (cols 8–9 on lg), intentionally empty. */}
          <div aria-hidden="true" className="hidden lg:col-span-2 lg:col-start-8 lg:block" />
        </div>
      </Section>

      {/* Full-width red CTA band sitting above the footer. */}
      <CtaBanner />
    </PageShell>
  );
}

/**
 * Hero search bar. On first load the bar morphs from a circle to the resting
 * 240px width (`learn-search-morph`); once that finishes, focus expands it to
 * 320px via the normal width transition. Typing live-filters the resources list
 * via the lifted `query` state. The light grey-50 background needs dark
 * foreground colours (`text-ink`). The search icon is a plain `<img>` (not
 * tinted) so it always paints.
 *
 * Placeholder: native `placeholder` stays transparent for a11y/layout; a visual
 * overlay shows "Search articles". On load (when motion is allowed) that overlay
 * reveals with a per-character stagger while the pill opens, then hands off to
 * the gradient shine sweep (`learn-search__placeholder`). The input is a `peer`
 * so the overlay hides via `:placeholder-shown` once the field has content.
 */
function HeroSearch({
  query,
  onQueryChange,
}: {
  query: string;
  onQueryChange: (value: string) => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  // After the char cascade finishes, swap to the shine placeholder. Starts true
  // when reduced-motion skips the cascade entirely.
  const [placeholderReady, setPlaceholderReady] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSearchOpen(true);
      setPlaceholderReady(true);
      return;
    }

    const chars = Array.from(SEARCH_PLACEHOLDER);
    const lastIndex = Math.max(chars.length - 1, 0);
    const doneAt =
      PLACEHOLDER_CHAR_BASE_MS + lastIndex * PLACEHOLDER_CHAR_STEP_MS + PLACEHOLDER_CHAR_ANIM_MS;
    const timer = window.setTimeout(() => setPlaceholderReady(true), doneAt);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="mt-10">
      <div
        className={cn(
          'relative mx-auto flex max-w-full items-center rounded-pill border-[1.5px] border-transparent bg-grey-50 py-2.5 text-ink focus-within:border-grey-900',
          searchOpen
            ? 'w-[240px] gap-3 px-4 transition-[width,border-color] duration-300 ease-nav focus-within:w-[320px]'
            : 'learn-search-morph',
        )}
        onAnimationEnd={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.animationName === 'learn-search-open') setSearchOpen(true);
        }}>
        <Icon name="search" className="size-[18px] shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') event.currentTarget.blur();
          }}
          placeholder={SEARCH_PLACEHOLDER}
          aria-label={SEARCH_PLACEHOLDER}
          className="peer min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-transparent"
        />
        {/* Visual-only placeholder overlay. Aligned to where the native
            placeholder sits (px-4 16px + 18px icon + gap-3 12px = 46px left).
            Hidden once the input has content via the peer :placeholder-shown. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-[46px] -translate-y-1/2 text-[16px] peer-[:not(:placeholder-shown)]:opacity-0">
          {placeholderReady ? (
            <span className="learn-search__placeholder">{SEARCH_PLACEHOLDER}</span>
          ) : (
            <PlaceholderChars text={SEARCH_PLACEHOLDER} />
          )}
        </span>
      </div>
    </div>
  );
}

/**
 * Per-character reveal for the search placeholder while the pill morphs open.
 * Spaces stay plain text; glyphs use `learn-char-in` with staggered delays.
 */
function PlaceholderChars({ text }: { text: string }) {
  return (
    <span className="learn-search__placeholder-chars text-grey-400">
      {Array.from(text).map((char, i) =>
        char === ' ' ? (
          <span key={i}> </span>
        ) : (
          <span
            key={i}
            className="learn-char-in"
            style={
              {
                '--stagger': `${PLACEHOLDER_CHAR_BASE_MS + i * PLACEHOLDER_CHAR_STEP_MS}ms`,
              } as CSSProperties
            }>
            {char}
          </span>
        ),
      )}
    </span>
  );
}
